import React, { useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currencyUtils';
import { formatDate } from '../utils/dateUtils';
import PaymentModal from './PaymentModal';
import CreditModal from './CreditModal';

const PaymentTab = ({ tenancyLedger, property, onDataUpdate, selectedListing }) => {
  const { agencyCurrency, user, agencyCountry } = useContext(AuthContext);

  // Helper function for consistent date formatting
  const formatDateWithContext = (dateString) => {
    return formatDate(dateString, {
      userPreference: user?.dateFormatPreference,
      agencyCountry: agencyCountry
    });
  };

  // Check if tenancy is active
  const tenancyStatus = selectedListing?.current_tenancy?.status;
  const isTenancyActive = tenancyStatus === 'active';

  // State for payments data with pagination
  const [payments, setPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [paymentsError, setPaymentsError] = useState(null);
  const [hasMorePayments, setHasMorePayments] = useState(true);
  const [nextCursor, setNextCursor] = useState(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);

  // Ref for scroll detection
  const tableContainerRef = useRef(null);

  // ── Credits state ────────────────────────────────────────────
  const [credits, setCredits] = useState([]);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [loadingMoreCredits, setLoadingMoreCredits] = useState(false);
  const [creditsError, setCreditsError] = useState(null);
  const [hasMoreCredits, setHasMoreCredits] = useState(false);
  const [creditsPage, setCreditsPage] = useState(1);

  // Credit modal state
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [editingCredit, setEditingCredit] = useState(null);

  // Ref for credits scroll detection
  const creditsTableRef = useRef(null);

  // Reset pagination state when tenancy ledger changes
  useEffect(() => {
    console.log('PaymentTab useEffect triggered:', {
      tenancyLedger: tenancyLedger,
      tenancyLedgerId: tenancyLedger?.id,
      hasLedger: !!tenancyLedger?.id
    });

    const ledgerId = tenancyLedger?.id;

    if (ledgerId) {
      console.log('Calling fetchPayments for tenancy ledger:', ledgerId);
      setPayments([]);
      setNextCursor(null);
      setHasMorePayments(true);
      fetchPayments(ledgerId, null, true); // Reset = true

      // Reset and fetch credits
      setCredits([]);
      setCreditsPage(1);
      setHasMoreCredits(false);
      fetchCredits(ledgerId, 1, true);
    } else {
      console.log('No tenancy ledger ID, clearing payments');
      setPayments([]);
      setCredits([]);
    }
  }, [tenancyLedger?.id]); // Watch only the lowercase id field

  const fetchPayments = useCallback(async (tenancyLedgerId, cursor = null, reset = false) => {
    console.log('fetchPayments called:', { tenancyLedgerId, cursor, reset });

    if (reset) {
      setLoadingPayments(true);
    } else {
      setLoadingMore(true);
    }
    setPaymentsError(null);

    try {
      const queryParams = new URLSearchParams({
        pagination_type: 'cursor',
        limit: '20',
        sort_by: 'payment_date',
        sort_dir: 'desc',
        forward: 'true',
        tenancy_ledger_id: tenancyLedgerId.toString()
      });

      // Add cursor if provided (for loading more)
      if (cursor) {
        queryParams.set('cursor', cursor);
      }

      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api';
      const url = `${baseUrl}/payments/search?${queryParams.toString()}`;

      console.log('Making API call to:', url);
      console.log('With headers:', {
        'Content-Type': 'application/json',
        'Authorization': token ? `Bearer ${token.substring(0, 10)}...` : 'No token'
      });

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      console.log('API response status:', response.status);

      if (!response.ok) {
        throw new Error(`Failed to fetch payments: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('API response data:', data);
      const newPayments = data.data || [];

      // Update payments list
      if (reset) {
        setPayments(newPayments);
      } else {
        setPayments(prevPayments => [...prevPayments, ...newPayments]);
      }

      // Update pagination state
      setNextCursor(data.next_cursor || null);
      setHasMorePayments(data.has_more || false);

    } catch (error) {
      console.error('Error fetching payments:', error);
      setPaymentsError(error.message);
      if (reset) {
        setPayments([]);
      }
    } finally {
      if (reset) {
        setLoadingPayments(false);
      } else {
        setLoadingMore(false);
      }
    }
  }, []);

  // Fetch credits for the tenancy ledger
  const fetchCredits = useCallback(async (tenancyLedgerId, page = 1, reset = false) => {
    if (reset) {
      setLoadingCredits(true);
    } else {
      setLoadingMoreCredits(true);
    }
    setCreditsError(null);

    try {
      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const url = `${baseUrl}/tenancy-ledgers/${tenancyLedgerId}/credits?page=${page}&limit=20`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch credits: ${response.statusText}`);
      }

      const data = await response.json();
      const newCredits = data.data || [];

      if (reset) {
        setCredits(newCredits);
      } else {
        setCredits(prev => [...prev, ...newCredits]);
      }

      setCreditsPage(page);
      setHasMoreCredits(data.has_more || false);
    } catch (error) {
      console.error('Error fetching credits:', error);
      setCreditsError(error.message);
      if (reset) setCredits([]);
    } finally {
      if (reset) {
        setLoadingCredits(false);
      } else {
        setLoadingMoreCredits(false);
      }
    }
  }, []);

  // Load more payments when scrolling near bottom
  const handleScroll = useCallback(() => {
    if (!tableContainerRef.current || loadingMore || !hasMorePayments || !nextCursor) return;

    const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;
    const scrollPosition = scrollTop + clientHeight;
    const threshold = scrollHeight - 100; // Load when 100px from bottom

    if (scrollPosition >= threshold && tenancyLedger?.id) {
      fetchPayments(tenancyLedger.id, nextCursor, false);
    }
  }, [loadingMore, hasMorePayments, nextCursor, tenancyLedger?.id, fetchPayments]);

  // Add scroll event listener
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Credits scroll handler
  const handleCreditsScroll = useCallback(() => {
    if (!creditsTableRef.current || loadingMoreCredits || !hasMoreCredits) return;

    const { scrollTop, scrollHeight, clientHeight } = creditsTableRef.current;
    if (scrollTop + clientHeight >= scrollHeight - 100 && tenancyLedger?.id) {
      fetchCredits(tenancyLedger.id, creditsPage + 1, false);
    }
  }, [loadingMoreCredits, hasMoreCredits, creditsPage, tenancyLedger?.id, fetchCredits]);

  useEffect(() => {
    const container = creditsTableRef.current;
    if (!container) return;
    container.addEventListener('scroll', handleCreditsScroll);
    return () => container.removeEventListener('scroll', handleCreditsScroll);
  }, [handleCreditsScroll]);

  const handleCreateCredit = () => {
    setEditingCredit(null);
    setShowCreditModal(true);
  };

  const handleSaveCredit = async (credit) => {
    setShowCreditModal(false);
    setEditingCredit(null);
    if (onDataUpdate) await onDataUpdate();
    if (tenancyLedger?.id) {
      setCredits([]);
      setCreditsPage(1);
      setHasMoreCredits(false);
      await fetchCredits(tenancyLedger.id, 1, true);
    }
  };

  const handleDeleteCredit = async (credit) => {
    const creditId = credit.id || credit.ID;
    if (!creditId) return;
    if (!window.confirm('Are you sure you want to delete this credit? This action cannot be undone.')) return;

    try {
      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const response = await fetch(`${baseUrl}/tenancy-ledgers/credits/${creditId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      if (!response.ok) throw new Error('Failed to delete credit.');
      if (onDataUpdate) await onDataUpdate();
      if (tenancyLedger?.id) {
        setCredits([]);
        setCreditsPage(1);
        setHasMoreCredits(false);
        await fetchCredits(tenancyLedger.id, 1, true);
      }
    } catch (error) {
      alert(error.message || 'Failed to delete credit.');
    }
  };

  const handleEditPayment = (payment) => {
    setEditingPayment(payment);
    setShowPaymentModal(true);
  };

  const handleCreatePayment = () => {
    setEditingPayment(null);
    setShowPaymentModal(true);
  };

  const handleSavePayment = async (payment) => {
    console.log('Payment saved:', payment);
    setShowPaymentModal(false);
    setEditingPayment(null);

    // Refresh both the tenancy ledger data and payments list
    if (onDataUpdate) {
      await onDataUpdate();
    }

    // Reset and reload payments list
    if (tenancyLedger?.id) {
      setPayments([]);
      setNextCursor(null);
      setHasMorePayments(true);
      await fetchPayments(tenancyLedger.id, null, true);
    }
  };

  const handleDeletePayment = async (payment) => {
    if (!payment?.ID && !payment?.id) return;

    // Check if payment is a bond transfer
    if (payment.BondTransferID || payment.bond_transfer_id) {
      alert('Bond payments cannot be deleted. Please reach out to your system administrator.');
      return;
    }

    // Check if payment is verified
    if ((payment.verification_status || payment.VerificationStatus) === 'verified') {
      alert('Cannot delete a verified payment. Verified payments that are linked to bank transactions cannot be deleted.');
      return;
    }

    // Improved confirmation message
    if (!window.confirm('Are you sure you want to delete this payment? This action cannot be undone.')) {
      return;
    }
    try {
      const paymentId = payment.ID || payment.id;
      const token = sessionStorage.getItem('token') ||
                   sessionStorage.getItem('access_token') ||
                   localStorage.getItem('token') ||
                   localStorage.getItem('access_token');
      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api/v1';
      const response = await fetch(`${baseUrl}/payments/${paymentId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });
      if (!response.ok) {
        throw new Error('Failed to delete payment.');
      }
      // Refresh both the tenancy ledger data and payments list
      if (onDataUpdate) {
        await onDataUpdate();
      }
      // Reset and reload payments list
      if (tenancyLedger?.id) {
        setPayments([]);
        setNextCursor(null);
        setHasMorePayments(true);
        await fetchPayments(tenancyLedger.id, null, true);
      }
    } catch (error) {
      alert(error.message || 'Failed to delete payment.');
    }
  };

  return (
    <>
      <div className="card border-0">
        <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
          <h5 className="mb-0">
            <i className="bi bi-cash-coin me-2"></i>
            Payments
          </h5>
          <span
            title={
              !tenancyLedger?.id
                ? 'No tenancy ledger available. Please ensure a tenancy exists.'
                : !isTenancyActive
                  ? 'Payments can only be added when tenancy is active. Current status: ' + (tenancyStatus || 'unknown')
                  : 'Add a new payment'
            }
            style={{ display: 'inline-block' }}
          >
            <button
              className="btn btn-light btn-sm"
              onClick={handleCreatePayment}
              disabled={!tenancyLedger?.id || !isTenancyActive}
              style={{
                cursor: (!tenancyLedger?.id || !isTenancyActive) ? 'not-allowed' : 'pointer',
                pointerEvents: (!tenancyLedger?.id || !isTenancyActive) ? 'none' : 'auto'
              }}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Payment
              {!isTenancyActive && tenancyLedger?.id && (
                <i className="bi bi-lock-fill ms-1" style={{ fontSize: '0.8rem' }}></i>
              )}
            </button>
          </span>
        </div>
        <div className="card-body">

          {/* Loading State - Initial Load */}
          {loadingPayments && (
            <div className="text-center py-4">
              <div className="spinner-border text-success" role="status">
                <span className="visually-hidden">Loading payments...</span>
              </div>
              <p className="text-muted mt-2">Loading payments...</p>
            </div>
          )}

          {/* Error State */}
          {paymentsError && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Error loading payments: {paymentsError}
              <button
                className="btn btn-outline-danger btn-sm ms-2"
                onClick={() => tenancyLedger?.id && fetchPayments(tenancyLedger.id, null, true)}
              >
                Retry
              </button>
            </div>
          )}

          {/* Payments Content */}
          {!loadingPayments && !paymentsError && (
            <>
              {payments.length > 0 ? (
                <div
                  ref={tableContainerRef}
                  className="table-responsive"
                  style={{ maxHeight: '600px', overflowY: 'auto' }}
                >
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Reference</th>
                        <th>Verification</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payments.map((payment, index) => {
                        const isBondTransferPayment = !!(payment.BondTransferID || payment.bond_transfer_id);
                        const verificationStatus = payment.verification_status || payment.VerificationStatus;
                        const isVerified = verificationStatus === 'verified';
                        const cannotDelete = isVerified || isBondTransferPayment;

                        let deleteDisabledReason = '';
                        if (isBondTransferPayment) {
                          deleteDisabledReason = 'Bond payments cannot be deleted. Please reach out to your system administrator.';
                        } else if (isVerified) {
                          deleteDisabledReason = 'Cannot delete a verified payment. Verified payments that are linked to bank transactions cannot be deleted.';
                        }

                        return (
                          <tr key={payment.ID || payment.id || index}>
                            <td>{formatDateWithContext(payment.PaymentDate || payment.payment_date || payment.date)}</td>
                            <td className="fw-semibold text-success">
                              {payment.amount?.formatted || formatCurrency(payment.amount?.amount / 100 || 0, agencyCurrency)}
                            </td>
                            <td>{payment.Reference || payment.reference || payment.transaction_ref || '-'}</td>
                            <td>
                              {verificationStatus === 'verified' ? (
                                <span className="badge bg-success">
                                  <i className="bi bi-check-circle me-1"></i>
                                  Verified
                                </span>
                              ) : verificationStatus === 'n/a' ? (
                                <span className="badge bg-secondary">
                                  <i className="bi bi-dash-circle me-1"></i>
                                  N/A
                                </span>
                              ) : (
                                <span className="badge bg-danger">
                                  <i className="bi bi-x-circle me-1"></i>
                                  Unverified
                                </span>
                              )}
                            </td>
                            <td>
                              <button
                                className="btn btn-outline-primary btn-sm me-2"
                                onClick={() => handleEditPayment(payment)}
                              >
                                <i className="bi bi-pencil me-1"></i>
                                {(isVerified || isBondTransferPayment) ? 'View' : 'Edit'}
                              </button>
                              <span
                                title={cannotDelete ? deleteDisabledReason : 'Delete this payment'}
                                style={{ display: 'inline-block' }}
                              >
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDeletePayment(payment)}
                                  disabled={cannotDelete}
                                  style={{
                                    cursor: cannotDelete ? 'not-allowed' : 'pointer',
                                    pointerEvents: cannotDelete ? 'none' : 'auto'
                                  }}
                                >
                                  <i className="bi bi-trash me-1"></i>
                                  Delete
                                </button>
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Loading More Indicator */}
                  {loadingMore && (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-success" role="status">
                        <span className="visually-hidden">Loading more...</span>
                      </div>
                      <small className="text-muted ms-2">Loading more payments...</small>
                    </div>
                  )}

                  {/* No More Data Indicator */}
                  {!hasMorePayments && payments.length > 0 && (
                    <div className="text-center py-3 text-muted">
                      <small>No more payments to load</small>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-cash-coin display-1 text-muted"></i>
                  <h5 className="mt-3 text-muted">No payments recorded</h5>
                  <p className="text-muted">Click "Add Payment" to record the first payment.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Credits Section ─────────────────────────────────────── */}
      <div className="card border-0 mt-4">
        <div className="card-header d-flex justify-content-between align-items-center" style={{ backgroundColor: '#8fbc9e' }}>
          <h5 className="mb-0 text-success-emphasis">
            <i className="bi bi-credit-card me-2"></i>
            Credits ({credits.length})
          </h5>
          <span
            title={
              !tenancyLedger?.id
                ? 'No tenancy ledger available.'
                : !isTenancyActive
                  ? 'Credits can only be added when tenancy is active. Current status: ' + (tenancyStatus || 'unknown')
                  : 'Add a new credit'
            }
            style={{ display: 'inline-block' }}
          >
            <button
              className="btn btn-light btn-sm"
              onClick={handleCreateCredit}
              disabled={!tenancyLedger?.id || !isTenancyActive}
              style={{
                cursor: (!tenancyLedger?.id || !isTenancyActive) ? 'not-allowed' : 'pointer',
                pointerEvents: (!tenancyLedger?.id || !isTenancyActive) ? 'none' : 'auto'
              }}
            >
              <i className="bi bi-plus-circle me-1"></i>
              Add Credit
              {!isTenancyActive && tenancyLedger?.id && (
                <i className="bi bi-lock-fill ms-1" style={{ fontSize: '0.8rem' }}></i>
              )}
            </button>
          </span>
        </div>
        <div className="card-body">

          {/* Loading State */}
          {loadingCredits && (
            <div className="text-center py-4">
              <div className="spinner-border text-info" role="status">
                <span className="visually-hidden">Loading credits...</span>
              </div>
              <p className="text-muted mt-2">Loading credits...</p>
            </div>
          )}

          {/* Error State */}
          {creditsError && (
            <div className="alert alert-danger">
              <i className="bi bi-exclamation-triangle me-2"></i>
              Error loading credits: {creditsError}
              <button
                className="btn btn-outline-danger btn-sm ms-2"
                onClick={() => tenancyLedger?.id && fetchCredits(tenancyLedger.id, 1, true)}
              >
                Retry
              </button>
            </div>
          )}

          {/* Credits Content */}
          {!loadingCredits && !creditsError && (
            <>
              {credits.length > 0 ? (
                <div
                  ref={creditsTableRef}
                  className="table-responsive"
                  style={{ maxHeight: '400px', overflowY: 'auto' }}
                >
                  <table className="table table-hover">
                    <thead className="table-light sticky-top">
                      <tr>
                        <th>Date</th>
                        <th>Amount</th>
                        <th>Type</th>
                        <th>Reason</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {credits.map((credit, index) => (
                        <tr key={credit.id || credit.ID || index}>
                          <td>{formatDateWithContext(credit.issued_at || credit.IssuedAt)}</td>
                          <td className="fw-semibold" style={{ color: '#8fbc9e' }}>
                            {credit.amount?.formatted ||
                              formatCurrency((credit.amount?.amount || 0) / 100, agencyCurrency)}
                          </td>
                          <td>
                            <span className="badge text-white" style={{ backgroundColor: '#8fbc9e' }}>
                              {credit.credit_type?.display_name || '-'}
                            </span>
                          </td>
                          <td>{credit.reason || '-'}</td>
                          <td>
                            <div className="d-flex gap-2 justify-content-end">
                              <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleDeleteCredit(credit)}
                              >
                                <i className="bi bi-trash me-1"></i>
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Loading More */}
                  {loadingMoreCredits && (
                    <div className="text-center py-3">
                      <div className="spinner-border spinner-border-sm text-info" role="status">
                        <span className="visually-hidden">Loading more...</span>
                      </div>
                      <small className="text-muted ms-2">Loading more credits...</small>
                    </div>
                  )}

                  {!hasMoreCredits && credits.length > 0 && (
                    <div className="text-center py-3 text-muted">
                      <small>No more credits to load</small>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="bi bi-credit-card display-1 text-muted"></i>
                  <h5 className="mt-3 text-muted">No credits recorded</h5>
                  <p className="text-muted">Click "Add Credit" to issue the first credit.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* PaymentModal */}
      <PaymentModal
        show={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        payment={editingPayment}
        tenancyLedgerId={tenancyLedger?.id}
        property={property}
        onSave={handleSavePayment}
      />

      {/* CreditModal */}
      <CreditModal
        show={showCreditModal}
        onClose={() => { setShowCreditModal(false); setEditingCredit(null); }}
        credit={editingCredit}
        tenancyLedgerId={tenancyLedger?.id}
        onSave={handleSaveCredit}
      />
    </>
  );
};

export default PaymentTab;
